import {
  Box,
  Button,
  Flex,
  Heading,
  Image,
  Link,
  Skeleton,
  Stack,
  Text,
} from "@chakra-ui/react";
import { FC, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Bulletins } from "../../../types/Bulletins/Bulletins";
import { getBulletins } from "../../../services/Bulletins/getBulletins";
import AlertComponent from "../../../components/atom/Alert/AlertComponent";
import { primaryColor } from "../../../utils/colors";
import { downloadPageToPDF } from "../../../helpers/downloadPageToPDF";
import BulletinDownloadTable from "./BulletinDownloadTable";
import { useForm } from "react-hook-form";
import { MONTH_NAMES } from "../../../utils/constants";
import { format } from "date-fns";
import { pt } from "date-fns/locale";
import TableAlert from "./TableAlert";
import TableTerrain from "./TableTerrain";
import { ExternalLinkIcon } from "@chakra-ui/icons";
import {
  renderEmptyState,
  renderLoadingState,
} from "../../../hooks/table/TableStates";
import { renderGraphs } from "./renderGraphs";
import { GraphBulletinInfo } from "../../../types/Graph/GraphBulletinInfo";
import TableRegion from "../../Indicator/Region/TableRegion";
import { BulletinIndicators } from "../../../types/Bulletins/BulletinIndicators";
import { BulletinDownload } from "../../../types/Bulletins/BulletinDownload";
import { IndicatorDataReportRegion } from "../../../types/Indicator/IndicatorDataReport/IndicatorDataReportRegion";
import TableOccurrencesAlertSan from "./TableOccurrencesAlertSan";

const BulletinsDownload: FC = () => {
  const { id } = useParams();
  const [data, setData] = useState<BulletinDownload>();
  const [dataBulletin, setDataBulletin] = useState<Bulletins | undefined>(
    undefined,
  );
  const pageRef = useRef<HTMLDivElement>(null);

  const [dataTableProvince, setDataTableProvince] =
    useState<BulletinIndicators>();
  const [dataTableCounty, setDataTableCounty] = useState<BulletinIndicators>();
  const [dataTableAlert, setDataTableAlert] = useState<any[]>([]);
  const [dataTableTerrain, setDataTableTerrain] = useState<any[]>([]);
  const [dataTableOccurrencesAlertSan, setDataTableOccurrencesAlertSan] =
    useState<any[]>([]);
  const [dataTableGraphs, setDataTableGraphs] = useState<GraphBulletinInfo[]>(
    [],
  );
  const [isLoaded, setIsLoaded] = useState<boolean>(false);

  const { setValue } = useForm();

  function formatDate(dateInput: any) {
    const date = dateInput instanceof Date ? dateInput : new Date(dateInput);

    if (isNaN(date.getTime())) {
      return "";
    }

    const formattedDate = format(date, "d 'de' MMMM 'de' yyyy", { locale: pt });
    const formattedTime = format(date, "HH'h'mm");

    return `Gerado a ${formattedDate} às ${formattedTime}`;
  }

  useEffect(() => {
    const fetchData = async () => {
      setIsLoaded(false);
      getBulletins(id)
        .then((result) => {
          setData(result.content);
          setDataBulletin(result.content.bulletin);
          setDataTableProvince(result.content.provinceKeyIndicators);
          setDataTableCounty(result.content.countyKeyIndicators);
          setDataTableAlert(result.content.occurrences);
          setDataTableTerrain(result.content.fieldOccurrences);
          setDataTableOccurrencesAlertSan(result.content.occurrenceAlertSans);
          setDataTableGraphs(result.content.indicatorsDataGraphs);
          setIsLoaded(true);
        })
        .catch((error) => {
          console.log("API Error:", error);
        });
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDownloadPDF = () => {
    if (pageRef.current) {
      downloadPageToPDF(pageRef.current, "boletim.pdf");
    }
  };
  const renderTableAlert = () => {
    return <TableAlert dataTable={dataTableAlert} isLoaded={true} />;
  };

  const renderTableTerrain = () => {
    return <TableTerrain dataTable={dataTableTerrain} isLoaded={true} />;
  };

  const renderTableOccurrencesAlertSan = () => {
    return (
      <TableOccurrencesAlertSan
        dataTable={dataTableOccurrencesAlertSan}
        isLoaded={true}
      />
    );
  };

  const renderContent = (renderTable: () => JSX.Element, tableData: any[]) => {
    if (!isLoaded) return renderLoadingState();

    return tableData.length > 0 ? renderTable() : renderEmptyState();
  };

  const renderIndicatorContent = (
    renderTable: () => JSX.Element,
    tableData: IndicatorDataReportRegion[] | undefined,
  ) => {
    if (!isLoaded) return renderLoadingState();

    return (tableData?.length ?? 0) > 0 ? renderTable() : renderEmptyState();
  };

  const renderIndicatorTable = (
    title: string,
    tableData: IndicatorDataReportRegion[],
    frequency: number,
  ) => {
    return (
      <BulletinDownloadTable
        title={title}
        renderContent={() =>
          renderIndicatorContent(
            () => renderTableRegion(tableData, frequency),
            tableData,
          )
        }
        spacing="1"
        fontSize="md"
      />
    );
  };

  const renderTableRegion = (tableData: any[], frequency: number) => {
    return (
      <TableRegion
        dataTable={tableData ?? []}
        isLoaded={true}
        setValue={setValue}
        frequency={frequency}
        date={
          dataBulletin?.createdAt
            ? (() => {
                const date = new Date(dataBulletin?.createdAt);
                date.setMonth(date.getMonth() - 1);
                return date;
              })()
            : null
        }
      />
    );
  };
  return (
    <Flex direction="column" minHeight="100vh" ref={pageRef} bg="white">
      <Box width="100%">
        <Box
          bgGradient="linear(80deg, #072952 5%, #42C958 100%)"
          height="25px"
          width="100%"
        />
        <Box paddingX="24" paddingTop="14" paddingBottom="24">
          <Flex justifyContent="space-between" alignItems="center">
            <Image
              src="/images/sisan.png"
              alt="Sistema de Informação e Alerta Rápido para a Segurança Alimentar e Nutricional"
            />
            <Flex direction="column" alignItems="flex-end" gap="4">
              {dataBulletin ? (
                <Heading as="h1" fontSize="4xl" color="black">
                  {`Boletim Mensal ${
                    dataBulletin?.county
                      ? `Municipal de ${dataBulletin?.county?.name}`
                      : `Provincial de ${dataBulletin?.province?.name}`
                  } - ${
                    dataBulletin?.month && MONTH_NAMES[dataBulletin?.month - 1]
                  } de ${dataBulletin?.year}`}
                </Heading>
              ) : (
                <Skeleton height="36px" width="60%" />
              )}
              {isLoaded ? (
                <Button onClick={handleDownloadPDF} variant="secondary">
                  Baixar o boletim
                </Button>
              ) : (
                <Skeleton height="40px" width="180px" />
              )}
            </Flex>
          </Flex>
          <Box as="hr" borderColor="gray.300" marginY="10" />
          <Flex width="100%" justifyContent="space-between" alignItems="center">
            <Flex direction="column" gap="4">
              <Heading as="h3" fontSize="2xl" color="black">
                Informação do boletim
              </Heading>
              {dataBulletin ? (
                <Stack direction="row" spacing={28}>
                  <Stack>
                    <Text fontWeight="bold" whiteSpace="nowrap" color="black">
                      Tipo de boletim:
                    </Text>
                    <Text fontWeight="bold" whiteSpace="nowrap" color="black">
                      Província:
                    </Text>
                    <Text fontWeight="bold" whiteSpace="nowrap" color="black">
                      Município:
                    </Text>
                    <Text fontWeight="bold" whiteSpace="nowrap" color="black">
                      Período:
                    </Text>
                    <Text fontWeight="bold" whiteSpace="nowrap" color="black">
                      Data de geração:
                    </Text>
                    <Text fontWeight="bold" whiteSpace="nowrap" color="black">
                      Emissor:
                    </Text>
                  </Stack>
                  <Stack>
                    <Text whiteSpace="nowrap" color="black">
                      {dataBulletin?.type?.name || "-"}
                    </Text>
                    <Text whiteSpace="nowrap" color="black">
                      {dataBulletin?.province?.name || "-"}
                    </Text>
                    <Text whiteSpace="nowrap" color="black">
                      {dataBulletin?.county?.name || "-"}
                    </Text>
                    <Text whiteSpace="nowrap" color="black">
                      {dataBulletin?.month && dataBulletin?.year
                        ? `${MONTH_NAMES[dataBulletin?.month - 1]} de ${
                            dataBulletin?.year
                          }`
                        : dataBulletin?.year || "-"}
                    </Text>
                    <Text whiteSpace="nowrap" color="black">
                      {formatDate(new Date())}
                    </Text>
                    <Text whiteSpace="nowrap" color="black">
                      Gerado automaticamente pelo sistema
                    </Text>
                  </Stack>
                </Stack>
              ) : (
                <Stack direction="row" spacing={28}>
                  <Stack>
                    <Skeleton height="20px" width="140px" />
                    <Skeleton height="20px" width="100px" />
                    <Skeleton height="20px" width="110px" />
                    <Skeleton height="20px" width="90px" />
                    <Skeleton height="20px" width="150px" />
                    <Skeleton height="20px" width="100px" />
                  </Stack>
                  <Stack>
                    <Skeleton height="20px" width="220px" />
                    <Skeleton height="20px" width="220px" />
                    <Skeleton height="20px" width="220px" />
                    <Skeleton height="20px" width="200px" />
                    <Skeleton height="20px" width="220px" />
                    <Skeleton height="20px" width="260px" />
                  </Stack>
                </Stack>
              )}
            </Flex>
            <Box width="40vw">
              <AlertComponent
                status="info"
                description="Este boletim foi gerado automaticamente pelo SISAN e contém gráficos e tabelas de indicadores fornecidos por entidades do Governo de Angola. A responsabilidade pela precisão e integridade dos dados apresentados é exclusivamente dessas entidades terceiras."
                borderColor="blue.500"
              />
            </Box>
          </Flex>
          <Box as="hr" borderColor="gray.300" marginY="10" />
          <Box paddingBottom="10">
            <AlertComponent
              status="info"
              description={
                <>
                  Pode consultar este boletim ou até informações mais detalhadas
                  sobre este Município no{" "}
                  <Link
                    href="https://sisan.leadershipbt.com/"
                    target="_blank"
                    color="blue.500"
                  >
                    SISAN
                  </Link>
                  .
                </>
              }
              borderColor="blue.500"
            />
          </Box>
          {dataTableGraphs.length > 0 &&
            dataTableGraphs.some((graph) => graph.data.length > 0) && (
              <>
                <Flex direction="column" gap="4">
                  <Heading as="h3" fontSize="2xl">
                    Painel de Controlo
                  </Heading>
                  {renderGraphs(dataTableGraphs)}
                </Flex>
                <Box as="hr" borderColor="gray.300" marginY="10" />
              </>
            )}
          {(data?.totalProvinceIndicators ?? 0) > 0 &&
            dataBulletin?.type?.id === 2 && (
              <>
                <Flex direction="column" gap="4">
                  <Heading as="h3" fontSize="2xl">
                    Indicadores Chave da Província
                  </Heading>

                  {dataTableProvince?.monthPeriod &&
                    (dataTableProvince?.monthPeriod?.length ?? 0) > 0 &&
                    renderIndicatorTable(
                      "Indicadores mensais",
                      dataTableProvince.monthPeriod,
                      3,
                    )}

                  {dataTableProvince?.trimestralPeriod &&
                    (dataTableProvince?.trimestralPeriod?.length ?? 0) > 0 &&
                    renderIndicatorTable(
                      "Indicadores trimestrais",
                      dataTableProvince?.trimestralPeriod,
                      4,
                    )}

                  {dataTableProvince?.semestralPeriod &&
                    (dataTableProvince?.semestralPeriod?.length ?? 0) > 0 &&
                    renderIndicatorTable(
                      "Indicadores semestrais",
                      dataTableProvince?.semestralPeriod,
                      5,
                    )}

                  {dataTableProvince?.yearPeriod &&
                    (dataTableProvince?.yearPeriod?.length ?? 0) > 0 &&
                    renderIndicatorTable(
                      "Indicadores anuais",
                      dataTableProvince?.yearPeriod,
                      6,
                    )}
                </Flex>
                <Box as="hr" borderColor="gray.300" marginY="10" />
              </>
            )}
          {(data?.totalCountyIndicators ?? 0) > 0 &&
            dataBulletin?.type?.id === 1 && (
              <>
                <Flex direction="column" gap="4">
                  <Heading as="h3" fontSize="2xl">
                    Indicadores Chave da Município
                  </Heading>

                  {dataTableCounty?.monthPeriod &&
                    (dataTableCounty?.monthPeriod?.length ?? 0) > 0 &&
                    renderIndicatorTable(
                      "Indicadores mensais",
                      dataTableCounty.monthPeriod,
                      3,
                    )}

                  {dataTableCounty?.trimestralPeriod &&
                    (dataTableCounty?.trimestralPeriod?.length ?? 0) > 0 &&
                    renderIndicatorTable(
                      "Indicadores trimestrais",
                      dataTableCounty?.trimestralPeriod,
                      4,
                    )}

                  {dataTableCounty?.semestralPeriod &&
                    (dataTableCounty?.semestralPeriod?.length ?? 0) > 0 &&
                    renderIndicatorTable(
                      "Indicadores semestrais",
                      dataTableCounty?.semestralPeriod,
                      5,
                    )}

                  {dataTableCounty?.yearPeriod &&
                    (dataTableCounty?.yearPeriod?.length ?? 0) > 0 &&
                    renderIndicatorTable(
                      "Indicadores anuais",
                      dataTableCounty?.yearPeriod,
                      6,
                    )}
                </Flex>
                <Box as="hr" borderColor="gray.300" marginY="10" />
              </>
            )}
          {dataTableAlert.length > 0 && (
            <>
              <BulletinDownloadTable
                title="Indicadores em Estado de Alerta"
                renderContent={() =>
                  renderContent(renderTableAlert, dataTableAlert)
                }
              />
              <Box as="hr" borderColor="gray.300" marginY="10" />
            </>
          )}
          {dataTableTerrain.length > 0 && (
            <BulletinDownloadTable
              title="Lista de Intervenções no Terreno"
              renderContent={() =>
                renderContent(renderTableTerrain, dataTableTerrain)
              }
              buttonElement={
                <Button
                  variant="secondary"
                  rightIcon={
                    <ExternalLinkIcon boxSize={4} verticalAlign="middle" />
                  }
                  onClick={() =>
                    window.open("/ocorrencias-no-terreno", "_blank")
                  }
                >
                  Adicionar intervenção no Terreno
                </Button>
              }
            />
          )}
          {dataTableOccurrencesAlertSan.length > 0 && (
            <BulletinDownloadTable
              title="Lista de Ocorrências de Alerta SAN"
              renderContent={() =>
                renderContent(
                  renderTableOccurrencesAlertSan,
                  dataTableOccurrencesAlertSan,
                )
              }
            />
          )}
        </Box>
      </Box>

      <Flex flexGrow={1} />

      <Flex
        width="100%"
        paddingX="24"
        paddingY="8"
        justifyContent="space-between"
        alignItems="center"
      >
        <Flex gap="12">
          <Image
            src="/images/sisan_logo_color.svg"
            alt="Sistema de Informação e Alerta Rápido para a Segurança Alimentar e Nutricional"
          />
          <Image
            src="/images/fresan_logo.svg"
            alt="Fortalecimento da resiliência e da segurança alimentar e nutricional em Angola"
          />
        </Flex>
        <Text fontStyle="normal" fontSize="md" color={primaryColor}>
          <Link
            href="https://sisan.leadershipbt.com/"
            target="_blank"
            color="blue.500"
          >
            www.sisan.ao | Projeto desenvolvido no âmbito do FRESAN
          </Link>
        </Text>
      </Flex>
    </Flex>
  );
};

export default BulletinsDownload;
